import { v4 as uuid } from 'uuid';

import { saveLocalGameSnapshot } from '~/app/controllerLocalWrapper';
import { resolveTrainingStorageScopeId, setCurrentStorageUserScopeId } from '~/app/storageScope';
import {
  getDebugGameTemplateByName,
  parseSnapshotGameName,
  prepareDebugGameInstance,
  prepareSnapshotGameInstance,
  type SnapshotRestoreVariant,
  type UserHorseRecord,
} from '~/server/src/debugGameRestore';
import type { GameSnapshot, GameState } from '~/server/src/dtos';
import { GameType } from '~/server/src/dtos';

export type LoadSnapshotAsTrainingResult =
  | { ok: true; gameState: GameState; storageScopeId: string }
  | { ok: false; error: string };

export const buildSnapshotTrainingSlug = (
  snapshotId: string,
  variant: SnapshotRestoreVariant = 0,
): string => `snapshot_${snapshotId}_${variant}`;

export function isDebugGameTrainingId(snapshotId: string): boolean {
  return getDebugGameTemplateByName(snapshotId) !== null;
}

export function loadDebugGameAsTraining({
  gameName,
  accountUserId,
  userHorses = [],
}: {
  gameName: string;
  accountUserId?: string;
  userHorses?: UserHorseRecord[];
}): LoadSnapshotAsTrainingResult {
  const template = getDebugGameTemplateByName(gameName);
  if (template === null) {
    return { ok: false, error: `Unknown debug game template: ${gameName}` };
  }

  const storageScopeId = resolveTrainingStorageScopeId({ accountUserId });
  const prepared = prepareDebugGameInstance(template, uuid(), storageScopeId, userHorses);
  if (!prepared.ok) {
    return { ok: false, error: prepared.error };
  }

  const gameState: GameState = {
    ...prepared.instance,
    gameType: GameType.Training,
  };

  setCurrentStorageUserScopeId(storageScopeId);
  saveLocalGameSnapshot(gameState, storageScopeId);

  return { ok: true, gameState, storageScopeId };
}

export function loadSnapshotAsTraining({
  snapshot,
  snapshotSlug,
  accountUserId,
  userHorses = [],
}: {
  snapshot: GameSnapshot;
  snapshotSlug: string;
  accountUserId?: string;
  userHorses?: UserHorseRecord[];
}): LoadSnapshotAsTrainingResult {
  const parsed = parseSnapshotGameName(snapshotSlug);
  const variant: SnapshotRestoreVariant = parsed?.variant ?? 0;
  const storageScopeId = resolveTrainingStorageScopeId({ accountUserId });
  const prepared = prepareSnapshotGameInstance(
    snapshot,
    uuid(),
    storageScopeId,
    variant,
    userHorses,
  );

  if (!prepared.ok) {
    return { ok: false, error: prepared.error };
  }

  const gameState: GameState = {
    ...prepared.envelope,
    gameType: GameType.Training,
    statePrev: snapshot.gameState.statePrev,
    statePrevPrev: snapshot.gameState.statePrevPrev,
  };

  setCurrentStorageUserScopeId(storageScopeId);
  saveLocalGameSnapshot(gameState, storageScopeId);

  return { ok: true, gameState, storageScopeId };
}
